import { createOwnId } from './sdk'
import type { OwnIdMock } from './types'

/**
 * Entry point. Loading this file (as the real CDN artifact would be loaded)
 * installs `window.ownid` and replays anything the app queued via the loader
 * stub before this script finished downloading.
 *
 * The app's loader defines:
 *   o.ownid = o.ownid || ((...a) => { (o.ownid.q = o.ownid.q || []).push(a); ... })
 * so queued calls live on `window.ownid.q` as arrays of arguments.
 */
declare global {
  interface Window {
    ownid?: OwnIdMock | ((...args: unknown[]) => unknown)
  }
}

;(function install() {
  // Capture the loader stub (and its queue) BEFORE we overwrite the global.
  const previous = window.ownid as
    | (((...args: unknown[]) => unknown) & { q?: unknown[][] })
    | undefined
  const queued = Array.isArray(previous?.q) ? previous!.q! : []

  // Register the custom elements the widget creates so they count as :defined.
  // Some hosts hide *undefined* custom elements — e.g. Shoelace ships
  // `:not(:defined){visibility:hidden}` — and the real OwnID SDK defines these
  // too. Without this, the login button / widget render invisible.
  for (const tag of ['ownid-root', 'ownid-standalone-button-widget']) {
    if (!customElements.get(tag)) {
      try {
        customElements.define(tag, class extends HTMLElement {})
      } catch {
        // already defined or unsupported — ignore
      }
    }
  }

  const ownid = createOwnId()
  window.ownid = ownid

  // Replay queued calls in the order they were made.
  for (const args of queued) {
    const [action, options] = args as [string, unknown?]
    ownid(action, options)
  }
})()
